<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  AGENT_NAME_INFORMATION_RETRIEVAL,
  AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL,
} from '@/constants';
import { MYPAGE_ROUTE_META } from '@/routers/constants';
import FeedHeader from '@/components/layouts/feed-header.vue';
import LeftMenu from '@/components/menu/left-menu.vue';
import ConfirmationModal from '@/components/modals/confirmation-modal.vue';
import TranslateNotificationModal from '@/components/modals/translate-notification-modal.vue';
import NotificationPopupDialog from '@/components/notifications/header/popup-dialog.vue';
import { useAppSetup } from '@/utils/composables/useAppSetup';
import { useExpandableLeftMenu } from '@/utils/composables/useExpandableLeftMenu';
import { useTranslateNotification } from '@/utils/composables/useTranslateNotification';
import { userSession } from '@/utils/userSession';
import { useStore } from '@/utils/vue';

const store = useStore();
const route = useRoute();

useAppSetup();

const confirmationModalOpen = computed(
  () => store.state.confirmationModal.confirmationModalOpen,
);
const {
  isExpanded,
  isMenuVisible,
  toggleIsFolded,
  handleMouseEnter,
  handleMouseLeave,
} = useExpandableLeftMenu(
  userSession.getLeftMenuIsExpanded,
  userSession.setLeftMenuIsExpanded,
);

const { isOpen: translateNotificationOpen, close: closeTranslateNotification } =
  useTranslateNotification();

const toggleIsFoldedByRouteType = () => {
  if (route.meta.type === 'search') {
    toggleIsFolded(false);
    isSearchMenuVisible.value = false;
  } else {
    toggleIsFolded();
  }
};

const isNotificationPages = computed(() =>
  route.matched.some(r => r.name === 'notifications'),
);
const isUserPage = computed(() => route.meta.type === 'user');
const isMyPage = computed(
  () =>
    route.meta.type === MYPAGE_ROUTE_META.type ||
    route.meta.type === 'userSettings',
);
const isSubNavigationEnable = computed(() => {
  return isMyPage.value || !(isUserPage.value || isNotificationPages.value);
});

const isSearchMenuVisible = ref(false);
watch(
  () => route.meta.type,
  (newRouteMetaType, oldRouteMetaType) => {
    if (oldRouteMetaType !== 'search' && newRouteMetaType === 'search') {
      // 別画面から検索画面に遷移する際にはメニューを開くように設定
      isSearchMenuVisible.value = true;
      isExpanded.value = true;
    } else if (oldRouteMetaType === 'search' && newRouteMetaType !== 'search') {
      // 検索画面から別画面に遷移する際にはisExpandedを再設定し、
      // isSearchMenuVisibleを初期化
      isSearchMenuVisible.value = false;
      isExpanded.value = userSession.getLeftMenuIsExpanded();
    }
  },
);
</script>

<template>
  <div class="p-main-view" tabindex="0">
    <div>
      <ConfirmationModal v-if="confirmationModalOpen"></ConfirmationModal>
      <TranslateNotificationModal
        v-if="translateNotificationOpen"
        @close="closeTranslateNotification"
      ></TranslateNotificationModal>
      <div id="modal-teleport-target"></div>
    </div>
    <div class="header">
      <FeedHeader
        :service-name="AGENT_NAME_INFORMATION_RETRIEVAL"
        :top-route-name="AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL"
      ></FeedHeader>
    </div>
    <NotificationPopupDialog></NotificationPopupDialog>
    <div class="p-feed-page-body">
      <div
        class="p-feed-left-menu-wrapper"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <div
          class="p-feed-left-menu"
          :class="{
            'p-feed-left-menu-no-subnavi':
              !isSubNavigationEnable || !isMenuVisible,
            'p-feed-left-menu-open':
              isSubNavigationEnable && (isMenuVisible || isSearchMenuVisible),
          }"
        >
          <LeftMenu
            :is-menu-visible="isMenuVisible || isSearchMenuVisible"
            :is-expanded="isExpanded"
            @toggle-is-folded="toggleIsFoldedByRouteType"
          ></LeftMenu>
        </div>
      </div>
      <div
        class="p-feed-content"
        :class="{
          'p-feed-content-no-subnavi': !isSubNavigationEnable || !isMenuVisible,
        }"
      >
        <router-view
          :is-menu-visible="isMenuVisible || isSearchMenuVisible"
        ></router-view>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
div.p-main-view {
  --layout-content-width: 948px;
  --layout-header-height: #{$header-height};
  // 旧レイアウトではヘッダーが隠れないため常に0
  --layout-header-offset: 0px;
  --layout-sticky-top: calc(
    var(--layout-header-height) + var(--layout-header-offset)
  );
  // タブ付きヘッダーの下でstickyにする要素の位置（ヘッダー64px + タブ41px + 余白7px）
  --layout-sticky-top-under-tabs: 112px;
  --layout-content-height: calc(100vh - var(--layout-header-height));
  --layout-left-menu-border-width: 1px;
  --layout-left-menu-expanded-width: 368px;
  --layout-left-menu-collapsed-width: #{$left-section-width};
  outline: none;

  .header {
    position: relative;
    z-index: var(--z-header);
  }

  div.p-feed-page-body {
    position: relative;
    display: flex;
    justify-content: start;

    div.p-feed-left-menu-wrapper {
      position: relative;
      z-index: var(--z-left-menu);

      div.p-feed-left-menu {
        position: fixed;
        width: calc(
          var(--layout-left-menu-expanded-width) +
            var(--layout-left-menu-border-width)
        );
        left: 0;
        top: var(--layout-header-height);
        background: #ffffff;
        height: var(--layout-content-height);
        &.p-feed-left-menu-open {
          border-right: var(--layout-left-menu-border-width) solid
            $color-gray400;
        }
      }

      div.p-feed-left-menu-no-subnavi {
        width: var(--layout-left-menu-collapsed-width);
      }
    }

    div.p-feed-content {
      --layout-left-menu-width: var(--layout-left-menu-expanded-width);
      position: relative;
      width: calc(100vw - var(--layout-left-menu-width));
      height: calc(var(--layout-content-height) - 80px);
      margin-top: var(--layout-header-height);
      margin-left: var(--layout-left-menu-width);
    }
    .p-feed-content > div {
      padding-bottom: 80px;
    }
    div.p-feed-content-no-subnavi {
      --layout-left-menu-width: var(--layout-left-menu-collapsed-width);
      width: calc(100vw - var(--layout-left-menu-width));
      margin-left: var(--layout-left-menu-width);
    }
  }
}
</style>
